import {
    type CSSProperties,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { usePluginSdkT } from "../i18n";
import { usePluginInteractionShield } from "./PluginSurfaceContext";

export interface ResizableSidebarLayoutProps {
    collapsed: boolean;
    sidebar: ReactNode;
    collapsedSidebar: ReactNode;
    children: ReactNode;
    storageKey?: string;
    defaultWidth?: number;
    minWidth?: number;
    maxWidth?: number;
    minMainWidth?: number;
    collapsedWidth?: number;
    className?: string;
    mainClassName?: string;
    sidebarClassName?: string;
    resizeHandleClassName?: string;
    title?: string;
}

const STORAGE_PREFIX = "magic-md/resizable-sidebar/";

export function clampResizableSidebarWidth(
    width: number,
    minWidth: number,
    maxWidth: number,
    containerWidth?: number,
    minMainWidth = 320,
): number {
    const dynamicMax = containerWidth
        ? Math.max(minWidth, Math.min(maxWidth, containerWidth - minMainWidth))
        : maxWidth;
    return Math.min(dynamicMax, Math.max(minWidth, width));
}

function readStoredWidth(key: string | undefined): number | null {
    if (!key) return null;
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + key);
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
}

function writeStoredWidth(key: string | undefined, width: number) {
    if (!key) return;
    try {
        localStorage.setItem(STORAGE_PREFIX + key, String(Math.round(width)));
    } catch {
        // Ignore quota and privacy mode failures.
    }
}

export function ResizableSidebarLayout({
    collapsed,
    sidebar,
    collapsedSidebar,
    children,
    storageKey,
    defaultWidth = 280,
    minWidth = 200,
    maxWidth = 560,
    minMainWidth = 320,
    collapsedWidth = 40,
    className,
    mainClassName,
    sidebarClassName,
    resizeHandleClassName,
    title,
}: ResizableSidebarLayoutProps) {
    const t = usePluginSdkT();
    const createInteractionShield = usePluginInteractionShield();
    const resolvedTitle = title ?? t("resizableSidebar.title");
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widthRef = useRef(defaultWidth);
    const dragState = useRef<{ startX: number; startWidth: number } | null>(null);
    const [width, setWidthState] = useState(() => (
        clampResizableSidebarWidth(readStoredWidth(storageKey) ?? defaultWidth, minWidth, maxWidth, undefined, minMainWidth)
    ));
    const [isDragging, setIsDragging] = useState(false);

    const clampWidth = useCallback(
        (next: number) => {
            const containerWidth = containerRef.current?.getBoundingClientRect().width;
            return clampResizableSidebarWidth(next, minWidth, maxWidth, containerWidth, minMainWidth);
        },
        [maxWidth, minMainWidth, minWidth],
    );

    const setWidth = useCallback(
        (next: number) => {
            const clamped = clampWidth(next);
            widthRef.current = clamped;
            setWidthState(clamped);
        },
        [clampWidth],
    );

    useEffect(() => {
        const next = clampWidth(readStoredWidth(storageKey) ?? defaultWidth);
        widthRef.current = next;
        setWidthState(next);
    }, [clampWidth, defaultWidth, storageKey]);

    useEffect(() => {
        const next = clampWidth(width);
        widthRef.current = next;
        if (next !== width) setWidthState(next);
    }, [clampWidth, width]);

    const handleMouseDown = useCallback(
        (event: ReactMouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            dragState.current = {
                startX: event.clientX,
                startWidth: widthRef.current,
            };
            setIsDragging(true);
        },
        [],
    );

    const resetWidth = useCallback(() => {
        setWidth(defaultWidth);
        writeStoredWidth(storageKey, defaultWidth);
    }, [defaultWidth, setWidth, storageKey]);

    useEffect(() => {
        if (!isDragging) return;

        const onMove = (event: MouseEvent) => {
            const state = dragState.current;
            if (!state) return;
            setWidth(state.startWidth + event.clientX - state.startX);
        };

        const onUp = () => {
            dragState.current = null;
            setIsDragging(false);
            writeStoredWidth(storageKey, widthRef.current);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        const removeInteractionShield = createInteractionShield("col-resize");

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            removeInteractionShield();
        };
    }, [createInteractionShield, isDragging, setWidth, storageKey]);

    const sidebarStyle: CSSProperties = {
        width: collapsed ? collapsedWidth : clampWidth(width),
    };

    return (
        <div
            ref={containerRef}
            className={`flex h-full min-h-0 items-stretch ${className ?? ""}`}
        >
            <div
                className={`min-h-full shrink-0 self-stretch ${sidebarClassName ?? ""}`}
                style={sidebarStyle}
            >
                {collapsed ? collapsedSidebar : sidebar}
            </div>

            {!collapsed && (
                <button
                    type="button"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={resolvedTitle}
                    title={resolvedTitle}
                    onMouseDown={handleMouseDown}
                    onDoubleClick={resetWidth}
                    className={`group relative z-10 -mx-[3.5px] w-2 shrink-0 self-stretch cursor-col-resize border-0 bg-transparent p-0 outline-none transition-colors ${
                        resizeHandleClassName ?? ""
                    }`}
                >
                    <span
                        aria-hidden="true"
                        className={`absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors ${
                            isDragging
                                ? "bg-accent"
                                : "bg-border/70 group-hover:bg-accent group-focus-visible:bg-accent"
                        }`}
                    />
                </button>
            )}

            <div className={`min-h-0 min-w-0 flex-1 self-stretch ${mainClassName ?? ""}`}>
                {children}
            </div>

            {isDragging && <div className="fixed inset-0 z-50 cursor-col-resize" />}
        </div>
    );
}
